import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-hot-toast";
import { Bell, Loader2 } from "lucide-react";
import { fetchApi, flmId, userRole } from "@/helpers/h";
import { useWebsocket } from "@/stores/websocket";
import { useNotifications } from "@/queries";
import { useNotificationCount } from "@/queries";
import AuthHelpers from "@/helpers/AuthHelpers";
import Helpers from "@/helpers/Helpers";
import {
  Dropdown,
  DropdownContent,
  DropdownTrigger,
} from "@/components/ui/dropdown";

function resolveNotificationUrl(notif) {
  const type = notif.notifiable_type;
  const data = notif.data;

  if (type === "new_sot")
    return `/service-engineer/jf/job/${data.service_order_ticket_id}`;
  if (type === "new_sot_flm")
    return `/flm/jf/job/${data.service_order_ticket_id}`;
  if (type === "new_rrt")
    return `/repair-center/rr/unclosed/${data.repair_request_ticket_id}`;
  if (type === "new_prt" || type === "bad_prt" || type === "not_avail_prt")
    return `/logistic/pr/unclosed/${data.part_request_ticket_id}`;
  if (type === "part_request_delivered")
    return `/service-engineer/sot/part-request/${data.part_request_ticket_id}`;
  if (type === "rjt_sot")
    return `/service-desk/sot/${data.service_order_ticket_id}/rejected`;
  if (type === "spn_sot" || type === "long_appointment")
    return `/service-desk/sot/${data.service_order_ticket_id}/history`;

  return "";
}

export default function NavbarNotifications() {
  const [trigger, setTrigger] = useState(false);
  const [triggerBrowserNotif, setTriggerBrowserNotif] = useState(false);
  const [page, setPage] = useState(1);
  const [accumulatedData, setAccumulatedData] = useState([]);
  const [meta, setMeta] = useState(null);
  const [readAllLoading, setReadAllLoading] = useState(false);
  const [markingReadId, setMarkingReadId] = useState(null);

  const notifiableId = useMemo(() => {
    const role = userRole();
    if (role === "service_engineer")
      return AuthHelpers.auth_service_engineer_id();
    if (role === "first_level_maintenance") return flmId();
    if (role === "admin") return AuthHelpers.auth_admin_id();
    return null;
  }, []);

  const notifParams = useMemo(() => {
    const params = { user_role: userRole() };
    if (notifiableId) params.notifiable_id = notifiableId;
    return params;
  }, [notifiableId]);

  const {
    data: paginatedData,
    isFetching,
    error,
  } = useNotifications(notifParams, page, 10, [trigger]);

  const { data: countData } = useNotificationCount(notifParams, [trigger]);
  const count = countData?.count ?? 0;

  useEffect(() => {
    setPage(1);
  }, [trigger]);

  useEffect(() => {
    if (!paginatedData?.data?.data) return;

    if (page === 1) {
      setAccumulatedData(paginatedData.data.data);
    } else {
      setAccumulatedData((prev) => [...prev, ...paginatedData.data.data]);
    }
    setMeta(paginatedData.data.meta || null);
  }, [paginatedData, page]);

  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  useEffect(() => {
    if (
      triggerBrowserNotif &&
      accumulatedData.length > 0 &&
      Helpers.isSupportedBrowser()
    ) {
      Notification.requestPermission().then((permission) => {
        if (permission === "granted") setTriggerBrowserNotif(false);
      });
    }
  }, [triggerBrowserNotif, accumulatedData]);

  const connection = useWebsocket((state) => state.connection);
  useEffect(() => {
    if (!connection) return;

    connection
      .channel("laravel_database_ticket-channel")
      .listen(".ticket-event", (event) => {
        setTrigger((prev) => !prev);
        setTriggerBrowserNotif(true);
        toast(event.message.data.msg, { icon: "🚀" });
      });

    return () => {
      connection.leaveChannel("laravel_database_ticket-channel");
    };
  }, [connection]);

  const handleMarkAsRead = async (notification_id) => {
    setMarkingReadId(notification_id);
    try {
      const res = await fetchApi({
        url: `/notification/${notification_id}/showed`,
        method: "GET",
      });
      if (res?.data?.success) {
        setTrigger((prev) => !prev);
      }
    } catch {
      // Error already toasted by fetchApi
    } finally {
      setMarkingReadId(null);
    }
  };

  const handleReadAll = async () => {
    setReadAllLoading(true);
    try {
      const res = await fetchApi({
        url: "/notification/read-all",
        method: "POST",
        data: notifParams,
      });
      if (res?.data?.success) {
        setTrigger((prev) => !prev);
      }
    } catch {
      // Error already toasted by fetchApi
    } finally {
      setReadAllLoading(false);
    }
  };

  const badgeCount = count > 99 ? "99+" : count > 9 ? "9+" : count;

  return (
    <Dropdown className="relative" awake={true}>
      <DropdownTrigger>
        <div className="relative p-2 rounded-lg hover:bg-gray-100 transition-colors duration-200 group">
          <div className="relative">
            <Bell
              className={`w-8 h-8 ${
                count !== 0 ? "text-red-500" : "text-gray-400"
              }`}
            />
            {count > 0 && (
              <div className="absolute -top-1 -right-1 w-6 h-6 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-xs text-white font-bold">
                  {badgeCount}
                </span>
              </div>
            )}
          </div>
        </div>
      </DropdownTrigger>
      <DropdownContent>
        <div className="w-80 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">Notifications</h3>
              {count > 0 && (
                <button
                  onClick={handleReadAll}
                  disabled={readAllLoading}
                  className="text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors disabled:opacity-50 flex items-center gap-1"
                >
                  {readAllLoading && (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  )}
                  Read All
                </button>
              )}
            </div>
            <p className="text-sm text-gray-500">{count} new notifications</p>
          </div>
          <div className="max-h-80 overflow-y-auto">
            {isFetching && accumulatedData.length === 0 ? (
              <div className="space-y-3 p-4">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3 animate-pulse">
                    <div className="w-2 h-2 mt-1.5 bg-gray-200 rounded-full flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                      <div className="h-3 bg-gray-200 rounded w-3/4" />
                      <div className="h-2 bg-gray-200 rounded w-1/2" />
                    </div>
                    <div className="h-6 w-12 bg-gray-200 rounded-full flex-shrink-0" />
                  </div>
                ))}
              </div>
            ) : accumulatedData.length !== 0 ? (
              accumulatedData.map((notif) => (
                <div
                  key={notif.id}
                  className="p-4 border-b border-gray-100 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="w-2 h-2 bg-red-500 rounded-full flex-shrink-0 mt-1" />
                        <p className="text-sm font-medium text-gray-900 line-clamp-2">
                          {notif.data.msg}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 ml-4">
                        {new Date(notif.created_at).toLocaleString()}
                      </p>
                    </div>
                    <Link
                      to={resolveNotificationUrl(notif)}
                      className="flex-shrink-0 px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 transition-colors disabled:opacity-50 flex items-center gap-1"
                      onClick={() => handleMarkAsRead(notif.id)}
                    >
                      {markingReadId === notif.id && (
                        <Loader2 className="w-3 h-3 animate-spin" />
                      )}
                      View
                    </Link>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                  <Bell className="w-6 h-6 text-gray-400" />
                </div>
                <p className="text-sm font-medium text-gray-900">
                  No notifications
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  You&apos;re all caught up!
                </p>
              </div>
            )}
            {(meta?.current_page ?? 1) < (meta?.last_page ?? 1) && (
              <button
                onClick={() => setPage((prev) => prev + 1)}
                disabled={isFetching}
                className="w-full py-3 text-sm font-medium text-blue-600 bg-gray-50 hover:bg-blue-50 transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
              >
                {isFetching ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            )}
          </div>
        </div>
      </DropdownContent>
    </Dropdown>
  );
}
